import * as React from 'react';

import { NavLink } from 'react-router-dom';
import ListItemText from '@mui/material/ListItemText';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ViewListIcon from '@mui/icons-material/ViewList';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalculateIcon from '@mui/icons-material/Calculate';
import ChecklistIcon from '@mui/icons-material/Checklist';

import { deepOrange, blueGrey, blue, indigo, deepPurple, teal, green } from '@mui/material/colors';

import { Avatar, Divider, ListItemAvatar, ListItemButton } from '@mui/material';
import { useAppSelector } from '../../hooks/hooks';
import {
    isEmployeeWarehouseOwner,
    isEmployeeWarehouseManager,
    isEmployeeProjectManager,
    isEmployeeProcurementManager,
    isEmployeeFinanceManager,
    isEmployeeProjectAdmin,
    canSeeProductTemplates,
} from '../../redux/selectors/employeeSelector';

type DefaultListItemsProps = {
    closeDrawer(): void;
};

export default function DefaultListItems(props: DefaultListItemsProps) {
    const { closeDrawer } = props;

    const isWarehouseOwner = useAppSelector(isEmployeeWarehouseOwner);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isProjectAdmin = useAppSelector(isEmployeeProjectAdmin);
    const canSeeTasks = isProjectManager || isWarehouseManager || isWarehouseOwner;
    const isProcurementManager = useAppSelector(isEmployeeProcurementManager);
    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);
    const canSeeTemplates = useAppSelector(canSeeProductTemplates);

    return (
        <div className="navBarItems">
            {canSeeTasks && (
                <ListItemButton component={NavLink} to="/tasks" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: green[600] }} className="navAvatar">
                            <ChecklistIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Moji zadaci" secondary="Čeka vaš potez" />
                </ListItemButton>
            )}
            {canSeeTasks && <Divider />}
            {isFinanceManager && (
                <ListItemButton component={NavLink} to="/" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: blueGrey[500] }} className="navAvatar">
                            <DashboardIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Pregled" secondary="Today" />
                </ListItemButton>
            )}
            {isFinanceManager && <Divider />}
            {(isProjectManager || isWarehouseManager || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/goods" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: indigo[400] }} className="navAvatar">
                            <InventoryIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Roba" />
                </ListItemButton>
            )}
            {(isProjectManager || isWarehouseManager || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/goodsByInvoice" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: indigo[700] }} className="navAvatar">
                            <ReceiptLongIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Roba po fakturi" />
                </ListItemButton>
            )}
            {(isProjectAdmin || isProjectManager || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/projects" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: deepPurple[500] }} className="navAvatar">
                            <AccountTreeIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Projekti" />
                </ListItemButton>
            )}
            {(isWarehouseManager || isWarehouseOwner || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/warehouses" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: blue[500] }} className="navAvatar">
                            <WarehouseIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Magacini" />
                </ListItemButton>
            )}
            {(isProjectManager || isWarehouseManager || isWarehouseOwner || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/constructionSites" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: blue[500] }} className="navAvatar">
                            <ConstructionIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Gradilišta" />
                </ListItemButton>
            )}
            <Divider />
            {(isProjectAdmin || isProjectManager || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/customers" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: blue[700] }} className="navAvatar">
                            <BusinessCenterIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Klijenti" />
                </ListItemButton>
            )}
            {(isProjectAdmin || isProcurementManager || isFinanceManager) && (
                <ListItemButton component={NavLink} to="/suppliers" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: blue[900] }} className="navAvatar">
                            <AssignmentIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Dobavljači" />
                </ListItemButton>
            )}
            {(isProjectManager || isFinanceManager) && <Divider />}
            {(isProjectManager || isWarehouseManager || isWarehouseOwner) && (
                <ListItemButton component={NavLink} to="/orders" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: deepPurple[500] }} className="navAvatar">
                            <ArticleIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Nalozi" />
                </ListItemButton>
            )}
            {(isProcurementManager || isWarehouseOwner) && (
                <ListItemButton component={NavLink} to="/purchaseOrders" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: deepPurple[800] }} className="navAvatar">
                            <ShoppingCartIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Nabavka" />
                </ListItemButton>
            )}
            {(isProjectManager || isWarehouseManager) && <Divider />}
            {(isProjectManager || isWarehouseManager) && (
                <ListItemButton component={NavLink} to="/articles" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: deepOrange[500] }} className="navAvatar">
                            <ViewListIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Artikli" />
                </ListItemButton>
            )}
            {canSeeTemplates && <Divider />}
            {canSeeTemplates && (
                <ListItemButton component={NavLink} to="/productTemplates" onClick={() => closeDrawer()}>
                    <ListItemAvatar>
                        <Avatar variant="rounded" sx={{ bgcolor: teal[600] }} className="navAvatar">
                            <CalculateIcon />
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary="Šabloni / cene" />
                </ListItemButton>
            )}
        </div>
    );
}
